import React, { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconEdit,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import moment from "moment-timezone";
import { useClient } from "../../context/ClientContext";
import { useSalon } from "../../context/SalonContext";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { Client, ClientFormulation, ClientNote } from "../../types/client";
import { BusinessInfo, SalonContextType } from "../../types/context";
import styles from "./ClientNotesPanel.module.css";

const MAX_CHAR_LIMIT = 1024;

interface ClientNotesPanelProps {
  client: Client;
  type: "note" | "formulation";
  onRequestRefresh: () => Promise<void>;
}

/**
 * Tab panel that lists a client's notes or formulations. Notes support a
 * `is_popup` toggle that surfaces them as a prominent alert in the client
 * header and on any transaction tied to the client.
 */
const ClientNotesPanel: React.FC<ClientNotesPanelProps> = ({
  client,
  type,
  onRequestRefresh,
}) => {
  const {
    createOrUpdateClientNote,
    deleteClientNote,
    createOrUpdateClientFormulation,
    deleteClientFormulation,
  } = useClient();
  const { businessInfo } = useSalon() as SalonContextType;
  const timezone =
    (businessInfo as BusinessInfo | null)?.timezone || "America/New_York";

  const isNoteType = type === "note";
  const items: (ClientNote | ClientFormulation)[] =
    (isNoteType ? client.notes : client.formulations) || [];

  const typeLabelSingular = isNoteType ? "Note" : "Formulation";
  const typeLabelPlural = isNoteType ? "Notes" : "Formulations";

  // Add state
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [newIsPopup, setNewIsPopup] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editIsPopup, setEditIsPopup] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    id: string;
  }>({ isOpen: false, id: "" });

  const formatDateTime = (dateStr: string) =>
    moment.utc(dateStr).tz(timezone).format("MMMM D, YYYY h:mm A");

  const upsert = (
    args:
      | { id?: string; text: string; is_popup?: boolean }
      | { id?: string; text: string },
  ) =>
    isNoteType
      ? createOrUpdateClientNote(client.id, args)
      : createOrUpdateClientFormulation(client.id, args);

  const remove = (id: string) =>
    isNoteType
      ? deleteClientNote(client.id, id)
      : deleteClientFormulation(client.id, id);

  const handleAdd = async () => {
    if (!newText.trim()) return;
    try {
      setIsSubmitting(true);
      await upsert(
        isNoteType
          ? { text: newText.trim(), is_popup: newIsPopup }
          : { text: newText.trim() },
      );
      setNewText("");
      setNewIsPopup(false);
      setIsAdding(false);
      await onRequestRefresh();
    } catch (e) {
      console.error(`Error adding ${type}:`, e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (item: ClientNote | ClientFormulation) => {
    setEditingId(item.id);
    setEditText(item.text);
    setEditIsPopup(isNoteType ? (item as ClientNote).is_popup === true : false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
    setEditIsPopup(false);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editText.trim()) return;
    try {
      setIsSubmitting(true);
      await upsert(
        isNoteType
          ? { id: editingId, text: editText.trim(), is_popup: editIsPopup }
          : { id: editingId, text: editText.trim() },
      );
      handleCancelEdit();
      await onRequestRefresh();
    } catch (e) {
      console.error(`Error updating ${type}:`, e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.id) return;
    try {
      setIsSubmitting(true);
      await remove(deleteConfirmation.id);
      setDeleteConfirmation({ isOpen: false, id: "" });
      await onRequestRefresh();
    } catch (e) {
      console.error(`Error deleting ${type}:`, e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAddForm = () => (
    <Paper className={styles.addForm} withBorder p="md" mb="md">
      <Text fw={600} size="sm" mb="xs">
        Add {typeLabelSingular}
      </Text>
      <Textarea
        placeholder={`Enter ${type}...`}
        value={newText}
        onChange={(e) => setNewText(e.target.value)}
        maxLength={MAX_CHAR_LIMIT}
        autosize
        minRows={2}
        maxRows={6}
      />
      {isNoteType && (
        <Group gap="xs" mt="xs" align="center">
          <IconAlertTriangle size={14} color="var(--brand-burnt-orange)" />
          <Switch
            size="sm"
            label="Show as popup alert"
            description="Surfaces this note prominently on the client and any transaction"
            color="var(--brand-burnt-orange)"
            checked={newIsPopup}
            onChange={(e) => setNewIsPopup(e.currentTarget.checked)}
          />
        </Group>
      )}
      <Group justify="space-between" mt="xs">
        <Text size="xs" c="dimmed">
          {newText.length}/{MAX_CHAR_LIMIT}
        </Text>
        <Group gap="xs">
          <Button
            variant="subtle"
            size="xs"
            onClick={() => {
              setIsAdding(false);
              setNewText("");
              setNewIsPopup(false);
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            onClick={handleAdd}
            disabled={isSubmitting || !newText.trim()}
            loading={isSubmitting}
          >
            Save
          </Button>
        </Group>
      </Group>
    </Paper>
  );

  const renderItem = (item: ClientNote | ClientFormulation) => {
    const isEditing = editingId === item.id;
    const isPopup = isNoteType && (item as ClientNote).is_popup === true;

    if (isEditing) {
      return (
        <Paper
          key={item.id}
          className={styles.card}
          withBorder
          p="md"
          data-type={type}
          data-popup={String(isPopup)}
        >
          <Text fw={700} size="lg" c="dark.8" mb="xs">
            {typeLabelSingular}
          </Text>
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            maxLength={MAX_CHAR_LIMIT}
            autosize
            minRows={2}
            maxRows={6}
          />
          {isNoteType && (
            <Group gap="xs" mt="xs" align="center">
              <IconAlertTriangle size={14} color="var(--brand-burnt-orange)" />
              <Switch
                size="sm"
                label="Show as popup alert"
                color="var(--brand-burnt-orange)"
                checked={editIsPopup}
                onChange={(e) => setEditIsPopup(e.currentTarget.checked)}
              />
            </Group>
          )}
          <Group justify="space-between" mt="xs">
            <Text size="xs" c="dimmed">
              {editText.length}/{MAX_CHAR_LIMIT}
            </Text>
            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
                aria-label={`Cancel editing ${type}`}
              >
                <IconX size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="teal"
                onClick={handleSaveEdit}
                disabled={isSubmitting || !editText.trim()}
                aria-label={`Save ${type}`}
              >
                {isSubmitting ? <Loader size={16} /> : <IconCheck size={16} />}
              </ActionIcon>
            </Group>
          </Group>
        </Paper>
      );
    }

    return (
      <Paper
        key={item.id}
        className={styles.card}
        withBorder
        p="md"
        data-type={type}
        data-popup={String(isPopup)}
      >
        <Group justify="space-between" align="flex-start" mb="xs">
          <Group gap="xs" align="center">
            <Text fw={700} size="lg" c="dark.8">
              {typeLabelSingular}
            </Text>
            {isPopup && (
              <Badge
                color="var(--brand-burnt-orange)"
                variant="filled"
                leftSection={<IconAlertTriangle size={10} />}
              >
                Popup
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => handleStartEdit(item)}
              aria-label={`Edit ${type}`}
            >
              <IconEdit size={14} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              size="sm"
              color="red"
              onClick={() =>
                setDeleteConfirmation({ isOpen: true, id: item.id })
              }
              aria-label={`Delete ${type}`}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        </Group>

        <Text size="md" c="dark.7" lh={1.6}>
          {item.text}
        </Text>

        <Group justify="space-between" mt="md" gap="md" wrap="wrap">
          <Text size="xs" c="dimmed">
            Created on {formatDateTime(item.created_dt)}
            {item.created_by_name ? ` by ${item.created_by_name}` : ""}
          </Text>
          <Text size="xs" c="dimmed">
            Last edited on {formatDateTime(item.updated_dt)}
            {item.updated_by_name ? ` by ${item.updated_by_name}` : ""}
          </Text>
        </Group>
      </Paper>
    );
  };

  return (
    <Paper className={styles.shell} p="lg" radius="lg" shadow="sm">
      <Stack gap="lg">
        <Group justify="space-between" align="center" wrap="wrap" gap="md">
          <Text size="lg" fw={600} c="dark.8">
            {typeLabelPlural}
          </Text>
          <Button
            size="sm"
            variant="default"
            color="teal"
            leftSection={<IconPlus size={16} />}
            onClick={() => setIsAdding(true)}
            disabled={isAdding}
          >
            Add {typeLabelSingular}
          </Button>
        </Group>

        {isAdding && renderAddForm()}

        {items.length === 0 ? (
          <Paper withBorder p="xl" className={styles.emptyState}>
            <Text c="dimmed" ta="center">
              No {typeLabelPlural.toLowerCase()} yet.
            </Text>
          </Paper>
        ) : (
          <Stack gap="md">{items.map(renderItem)}</Stack>
        )}

        <DeleteConfirmationModal
          isOpen={deleteConfirmation.isOpen}
          onClose={() => setDeleteConfirmation({ isOpen: false, id: "" })}
          onConfirm={handleDeleteConfirm}
          title={`Delete ${typeLabelSingular}`}
          message={`Are you sure you want to delete this ${type}? This action cannot be undone.`}
        />
      </Stack>
    </Paper>
  );
};

export default ClientNotesPanel;
